import promise from 'promise';
import DispatchConstants from 'app/shared/constants/DispatchConstants';
import UserPermissionsConstants from 'app/shared/constants/UserPermissionsConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';

import Company from 'app/shared/model/Company';
import CompanyUser from 'app/shared/model/CompanyUser';
import CompanyProperty from 'app/shared/model/CompanyProperty';
import UserManagementActivityLog from 'app/shared/model/UserManagementActivityLog';

import api from '../utils/api';

/* eslint-enable max-len */
const UserManagementActions = {
    getInitialUserListings() {
        return (dispatch, getState) => {
            dispatch({
                type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_LOADING,
            });
            const { currentUser } = getState().userManagement;
            return dispatch(api.users.company.getListingsForEmail(currentUser.email))
                .then(res => dispatch({
                    type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_SUCCESS,
                    payload: {
                        properties: res.response.listingKeys
                            .map(listingKey => ({
                                listingId: `${listingKey.feedListingId}_${listingKey.feedId}`,
                            })),
                    },
                }))
                .catch(() => dispatch({
                    type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_FAIL,
                }));
        };
    },
    setCompanyName(companyName) {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_SET_COMPANY_NAME,
            payload: { companyName },
        });
    },
    getCompany() {
        return (dispatch) => {
            dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_COMPANY_LOADING });
            return dispatch(api.users.company.getUserCompanyStats())
                .then((result) => {
                    if (result.success) {
                        const company = Company(result.response);
                        return dispatch({
                            type: DispatchConstants.USER_MANAGEMENT_GET_COMPANY_SUCCESS,
                            payload: { company },
                        });
                    }
                    return dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_GET_COMPANY_FAIL,
                        payload: { error: result.error },
                    });
                });
        };
    },
    getUserManagementProfiles() {
        return (dispatch) => {
            dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_USERS_LOADING });
            return dispatch(api.users.company.getUserManagementProfiles())
                .then((result) => {
                    if (result.success) {
                        const users = result.response.userProfiles.map(user => CompanyUser(user));
                        return dispatch({
                            type: DispatchConstants.USER_MANAGEMENT_GET_USERS_SUCCESS,
                            payload: { users },
                        });
                    }
                    return dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_GET_USERS_FAIL,
                        payload: { error: result.error },
                    });
                });
        };
    },
    getCompanyUsers() {
        return (dispatch) => {
            dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_USERS_LOADING });
            return dispatch(api.users.company.getCompanyUsers())
                .then((result) => {
                    if (result.success) {
                        const users = result.response.userProfiles.map(user => CompanyUser(user));
                        return dispatch({
                            type: DispatchConstants.USER_MANAGEMENT_GET_USERS_SUCCESS,
                            payload: { users },
                        });
                    }
                    return dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_GET_USERS_FAIL,
                        payload: { error: result.error },
                    });
                });
        };
    },
    updateUserSettings(params) {
        return (dispatch) => {
            dispatch({ type: DispatchConstants.USER_MANAGEMENT_UPDATE_USERS_LOADING });
            return dispatch(api.users.company.updateUserSettings(params))
                .then((result) => {
                    if (result.success) {
                        return dispatch({
                            type: DispatchConstants.USER_MANAGEMENT_UPDATE_USERS_SUCCESS,
                            payload: {},
                        });
                    }
                    return dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_UPDATE_USERS_FAIL,
                        payload: { error: result.error },
                    });
                });
        };
    },
    getCurrentUserInfo(userBrand, userToken) {
        return (dispatch) => {
            dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_LOADING });
            return dispatch(api.users.company.getUserSettings({ userBrand, userToken }))
                .then((result) => {
                    if (result.success) {
                        const user = new CompanyUser(result.response.userProfiles[0]);

                        return dispatch({
                            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_SUCCESS,
                            payload: { user },
                        });
                    }
                    const errorMessage = (result.error && result.error.message) || 'Failed to get user information.';
                    return dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_FAIL,
                        payload: { error: errorMessage },
                    });
                });
        };
    },
    getCurrentUserProperties(userBrand, userToken) {
        return (dispatch) => {
            dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_LOADING });
            return dispatch(api.users.company.getUserCompanyProperties({ userBrand, userToken }))
                .then((result) => {
                    if (result.success) {
                        const properties = result.response.properties.map(property => CompanyProperty(property));

                        return dispatch({
                            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_SUCCESS,
                            payload: { properties },
                        });
                    }
                    return dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_FAIL,
                        payload: { error: result.error },
                    });
                });
        };
    },
    getAvailableProperties(userBrand, userToken) {
        return dispatch => dispatch(api.users.company.getUserCompanyProperties({ userBrand, userToken }))
            .then((result) => {
                if (result.success) {
                    const availableProperties = result.response.properties
                        .map(property => CompanyProperty(property));

                    return dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_AVAILABLE_PROPERTIES,
                        payload: { availableProperties },
                    });
                }
            });
    },
    getPendingProfile(email) {
        return (dispatch) => {
            dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_LOADING });
            return dispatch(api.users.company.getPendingProfile({ email }))
                .then((result) => {
                    if (result.success) {
                        const user = new CompanyUser(result.response.userProfiles[0]);

                        return dispatch({
                            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_SUCCESS,
                            payload: { user },
                        });
                    }
                    return dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_FAIL,
                        payload: { error: result.error },
                    });
                });
        };
    },
    getPendingUserProperties(email) {
        return (dispatch) => {
            dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_LOADING });
            return dispatch(api.users.company.getPendingUserProperties({ email }))
                .then((result) => {
                    if (result.success) {
                        const properties = result.response.properties.map(property => CompanyProperty(property));

                        return dispatch({
                            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_SUCCESS,
                            payload: { properties },
                        });
                    }
                    return dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_FAIL,
                        payload: { error: result.error },
                    });
                });
        };
    },
    verifyEmail(email) {
        return dispatch => dispatch(api.users.company.verifyEmail({ email }))
            .then((response) => {
                const { error: errorRMWModel, success } = response;
                if (success) {
                    return promise.resolve();
                }
                return promise.reject(errorRMWModel);
            });
    },
    setUsersNotification(notification) {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_SET_USERS_NOTIFICATION,
            payload: {
                notification,
            },
        });
    },
    createUserInvitation(userInfo) {
        return dispatch => dispatch(api.users.company.createUserInvitation(userInfo))
            .then((response) => {
                const { success } = response;
                if (success) {
                    dispatch(this.setUsersNotification(`You've invited ${userInfo.userName} to your company! 
                    We'll let you know when they accept.`));
                    return promise.resolve();
                }
                return promise.reject('Failed to create new user');
            });
    },
    resendInviteEmail(user) {
        return dispatch => dispatch(api.users.company.resendInviteEmail(user.email))
            .then((response) => {
                const { success } = response;
                if (success) {
                    dispatch(this.setUsersNotification(`Invite email has been resent to ${user.email}`));
                    return promise.resolve();
                }
                return promise.reject('Failed to resend invite email.');
            });
    },
    onboardPendingUser() {
        return dispatch => dispatch(api.users.company.onboardPendingUser())
            .then((response) => {
                const { success } = response;
                if (success) {
                    return promise.resolve();
                }
                return promise.reject();
            });
    },
    cancelInvitation(email) {
        return dispatch => dispatch(api.users.company.cancelInvitation(email))
            .then((response) => {
                const { success } = response;
                if (success) {
                    return promise.resolve();
                }
                return promise.reject();
            });
    },
    updateUserInvitation(userInfo) {
        return dispatch => dispatch(api.users.company.updateUserInvitation(userInfo))
            .then((response) => {
                const { success } = response;
                if (success) {
                    return promise.resolve();
                }
                return promise.reject('Failed to create new user');
            });
    },
    editableUserSetPermission(permission, value) {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_SET_EDITABLE_USER_PERMISSION,
            payload: {
                permission,
                value,
            },
        });
    },
    currentUserSetPermissions(permissions) {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_PERMISSIONS,
            payload: {
                permissions,
            },
        });
    },
    currentUserSetProperties(properties) {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_PROPERTIES,
            payload: {
                properties,
            },
        });
    },
    currentUserSetName(userName) {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_NAME,
            payload: {
                userName,
            },
        });
    },
    currentUserSetEmail(email) {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_EMAIL,
            payload: {
                email,
            },
        });
    },
    editableUserSetLoadingState(loadingState) {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_SET_EDITABLE_USER_LOADING_STATE,
            payload: {
                loadingState,
            },
        });
    },
    editableUserSave() {
        return async (dispatch, getState) => {
            dispatch(this.editableUserSetLoadingState(LoadingConstants.LOADING));
            const { currentUser } = getState().userManagement;
            const privileges = Object.keys(currentUser.permissions)
                .filter(roleName => currentUser.permissions[roleName])
                .map(roleName => UserPermissionsConstants.PERMISSION_ROLES[roleName]);
            const params = {
                email: currentUser.email,
                userName: currentUser.userName,
                privileges,
            };
            if (!privileges[UserPermissionsConstants.PERMISSION_ROLES.COMPANY_ADMIN]
                || !privileges[UserPermissionsConstants.PERMISSION_ROLES.SYNC_PROPERTIES]) {
                params.properties = currentUser.properties.map(property => property.listingId);
            }
            try {
                if (currentUser.isPending) {
                    await dispatch(this.updateUserInvitation(params));
                } else if (!currentUser.userToken) {
                    await dispatch(this.createUserInvitation(params));
                } else {
                    params.userBrand = currentUser.userBrand;
                    params.userToken = currentUser.userToken;
                    await dispatch(this.updateUserSettings(params));
                }
                dispatch(this.editableUserSetLoadingState(LoadingConstants.SUCCESS));
            } catch (e) {
                dispatch(this.editableUserSetLoadingState(LoadingConstants.ERROR, 'Error saving changes'));
            }
        };
    },
    initInvitation() {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_INIT_INVITATION,
        });
    },
    currentUserInit() {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_CURRENT_USER_INIT,
        });
    },
    deactivateUser() {
        return async (dispatch, getState) => {
            const { currentUser } = getState().userManagement;
            return dispatch(api.users.company.deactivateUser(currentUser.userBrand,
                currentUser.userToken));
        };
    },
    initActivityHistory() {
        return ({
            type: DispatchConstants.USER_MANAGEMENT_ACTIVITY_HISTORY_INIT,
        });
    },
    getActivityHistory() {
        return async (dispatch, getState) => {
            const { activityHistory } = getState().userManagement;
            if (activityHistory.loadingState === LoadingConstants.ERROR) {
                return;
            }
            dispatch({
                type: DispatchConstants.USER_MANAGEMENT_SET_ACTIVITY_HISTORY_LOADING_STATE,
                payload: {
                    loadingState: LoadingConstants.LOADING,
                },
            });
            try {
                let startTimestamp;
                if (activityHistory.logs.length > 0) {
                    startTimestamp = activityHistory.logs[activityHistory.logs.length - 1].reverseCreatedMs;
                }
                const response = await dispatch(api.users.company.getActivityHistory(startTimestamp));
                if (!response.success) {
                    dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_SET_ACTIVITY_HISTORY_ERROR,
                        payload: {
                            error: 'Failed to get activity history',
                        },
                    });
                } else {
                    const { userManagementActivityList, pageSize } = response.response;
                    dispatch({
                        type: DispatchConstants.USER_MANAGEMENT_SET_ACTIVITY_HISTORY,
                        payload: {
                            logs: userManagementActivityList.map(UserManagementActivityLog),
                            pageSize,
                        },
                    });
                }
            } catch (e) {
                dispatch({
                    type: DispatchConstants.USER_MANAGEMENT_SET_ACTIVITY_HISTORY_ERROR,
                    payload: {
                        error: 'Failed to get activity history',
                    },
                });
            }
        };
    },
};

export default UserManagementActions;
